import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "app/store";
import { AppTooltip } from "components/AppTooltip/AppTooltip";
import { openWhatsNewModal } from "../state/patch-notes-slice";

/**
 * Floating button that brings the dismissed "What's New" modal back.
 *
 * There is no floating-action-button pattern anywhere else in this app, so its positioning and
 * stacking are net-new rather than copied — see _patch-notes.scss for the z-index reasoning.
 * Small-viewport placement is deliberately minimal for v1 (icon only); the mockup is
 * desktop-only and doesn't answer mobile.
 *
 * Only rendered after the user has set the modal aside (X / backdrop) in this session, so it
 * never competes with the modal itself and never follows a confirmed "Got it". Once
 * acknowledged, the way back in is the nav entry to the full history page.
 */
export default function WhatsNewReopenButton() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const hasLoginData = useSelector((state: RootState) => state.authentificationSlice.hasLoginData);
    const termsAcceptanceRequired = useSelector((state: RootState) => state.authentificationSlice.termsAcceptanceRequired);
    const { modalOpen, dismissedThisSession, acknowledged, current } = useSelector((state: RootState) => state.patchNotesSlice);

    // Nothing to reopen if there is no release, or if the modal is already up.
    // Hidden behind the Terms gate: a floating button over a blocking modal invites a click
    // that cannot do anything.
    // `acknowledged` is what makes this a "come back to it" affordance rather than an unread
    // badge — the user who pressed "Got it" is done, and only the one who set the modal aside
    // is offered it back.
    if (!hasLoginData || termsAcceptanceRequired || !dismissedThisSession || acknowledged || modalOpen || current === null) {
        return null;
    }

    return (
        <AppTooltip title={t('patchNotes.title')}>
            <button
                type="button"
                id="pn-reopen-btn"
                // pn-scope is required, not decorative: it is what defines the --zt-* custom
                // properties (_mockup-tokens.scss). This button renders as a direct child of the
                // app shell rather than inside the modal or the page, so without the class the
                // tokens do not resolve and border-radius/background silently fall back to their
                // initial values - a square, transparent button.
                className="pn-reopen-fab pn-scope"
                aria-label={t('patchNotes.title')!}
                onClick={() => dispatch(openWhatsNewModal())}>

                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 11l18-5v12L3 14v-3z" />
                    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                </svg>
                <span className="pn-reopen-label">{t('patchNotes.title')}</span>
            </button>
        </AppTooltip>
    );
}
